/**
 * RequireAccessLevel — route-level RBAC guard.
 *
 * Phase-2 Wave-2 W2.T7. Sits inside `<RequireAuth>` (which already
 * verifies the user is logged in and has cleared `mustChangePassword`)
 * and gates a route by the user's `AccessLevel`. Mismatches redirect to
 * `/forbidden` so deep links don't leak protected page chrome.
 *
 * Two prop shapes are accepted so call-sites read naturally for both
 * "minimum tier" and "explicit allow-list" semantics:
 *
 *   <RequireAccessLevel minAccessLevel="MANAGER">…</RequireAccessLevel>
 *     → ADMIN, MANAGER, SUPERVISOR allowed; EMPLOYEE blocked.
 *
 *   <RequireAccessLevel allowedAccessLevels={['MANAGER', 'ADMIN']}>…
 *     → ONLY those two levels (Phase-2 PLAN W3.T3: Сотрудники page
 *     blocks both EMPLOYEE and SUPERVISOR per V2-FIX-07 / F-01).
 *
 * The "min tier" form intentionally treats the levels as a 4-step
 * ordering (EMPLOYEE < MANAGER < ADMIN < SUPERVISOR). When that ordering
 * is wrong for the page (the Employees page is the canonical example —
 * it must NOT include SUPERVISOR), use `allowedAccessLevels` so the
 * matrix is explicit at the call-site.
 *
 * Loading and error paths inherit from `useMe()` so we don't double-render
 * a skeleton when `<RequireAuth>` is already showing one. If `useMe`
 * returns no data after loading completes (auth glitch), the user is sent
 * to `/login` rather than `/forbidden` — they're not logged in anymore,
 * not authorised-but-blocked.
 */
import * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import type { AccessLevel } from '@aris/shared';

import { Skeleton } from '@/components/ui/skeleton';
import { isMeHydrating, isSessionLost, useMe } from '@/hooks/use-me';

const MIN_TIER_ORDER: Readonly<Record<AccessLevel, number>> = {
  EMPLOYEE: 0,
  MANAGER: 1,
  ADMIN: 2,
  SUPERVISOR: 3,
};

type RequireAccessLevelProps =
  | {
      minAccessLevel: AccessLevel;
      allowedAccessLevels?: never;
      children?: React.ReactNode;
    }
  | {
      allowedAccessLevels: readonly AccessLevel[];
      minAccessLevel?: never;
      children?: React.ReactNode;
    };

export function RequireAccessLevel(props: RequireAccessLevelProps): React.ReactElement {
  const { children } = props;
  const location = useLocation();
  const { data: me, isLoading, isError, isFetching, refetch } = useMe();

  // Hard-nav fix (2026-06-10, fix-pack-2): `me` can be present but NOT yet
  // carry `accessLevel`. With the BE fix-pack-2 login/refresh now seed the
  // full shape, so the only remaining source is a future payload drift —
  // but the guard must still never run the allowlist against `undefined`
  // (that matches NO tier and bounced an authorised SUPERVISOR to
  // /forbidden during the un-hydrated window; SPA nav worked only because
  // the full `/auth/me` was already cached).
  //
  // Bounded refetch (fix-pack-2 item 3): if the cache settles on a
  // level-less `me` while no fetch is in flight, force `/auth/me` ONCE to
  // re-hydrate. The one-shot latch (refetchedRef) prevents a refetch storm
  // when the server keeps returning a partial body (DA finding 1). `!isError`
  // is folded into the hydrate condition so a settled 5xx falls THROUGH to
  // the /login fallback instead of looping (CR finding) — without it the
  // isError branch was unreachable while partial data was retained.
  const hydrating = isMeHydrating(me) && !isError;
  const refetchedRef = React.useRef(false);

  // Re-arm the latch once `me` is fully hydrated, so a LATER drift on the
  // same mount gets its own single refetch rather than being permanently
  // locked out by a stale `true`.
  React.useEffect(() => {
    if (me && me.accessLevel != null) refetchedRef.current = false;
  }, [me]);

  React.useEffect(() => {
    if (hydrating && !isFetching && !refetchedRef.current) {
      refetchedRef.current = true;
      void refetch();
    }
  }, [hydrating, isFetching, refetch]);

  // Hold the loading skeleton while we still have a hydration attempt left:
  // either a fetch is in flight, or we have not yet fired our one-shot
  // refetch. Once that single refetch settles and `me` is STILL partial,
  // we stop holding and fall through to the /login glitch fallback below.
  const holdForHydration = hydrating && (isFetching || !refetchedRef.current);

  if (isLoading || holdForHydration) {
    return (
      <div data-testid="require-access-level-loading" className="space-y-3 p-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // No user payload after loading completes → fall back to login. The
  // outer `<RequireAuth>` should have already redirected, so we only land
  // here on rare races (token revoked + cache stale). A `me` that is STILL
  // level-less after our one forced refetch is treated as the same class
  // of auth glitch (persistent partial body) → /login, never /forbidden,
  // so we don't deny an authorised user on malformed data.
  //
  // ⚠️ НЕ `isError || …` (Ф8 · В-О2, фикс-пак 2). Так стояло — и с тех пор, как
  // профиль перезапрашивается на каждом переходе, любой отказ этого
  // перезапроса при живой сессии (сеть, 502, 429) выбрасывал человека на вход
  // со всех маршрутов под этим стражем. Правило теперь общее с `<RequireAuth>`
  // — `isSessionLost`: уводит отсутствие профиля либо сброшенный токен, а не
  // ошибка запроса при имеющемся профиле.
  //
  // Профиль БЕЗ уровня доступа остаётся отдельной причиной: это не «отказ
  // перезапроса при годном профиле», а негодный профиль — пускать по нему
  // некуда. `if (!me …)` повторено ради сужения типа.
  if (isSessionLost({ me, isError }) || !me || me.accessLevel == null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const allowed = computeAllowed(props, me.accessLevel);
  if (!allowed) {
    return <Navigate to="/forbidden" replace state={{ from: location.pathname }} />;
  }

  return <>{children ?? <Outlet />}</>;
}

function computeAllowed(props: RequireAccessLevelProps, actorLevel: AccessLevel): boolean {
  if ('allowedAccessLevels' in props && props.allowedAccessLevels) {
    return props.allowedAccessLevels.includes(actorLevel);
  }
  if ('minAccessLevel' in props && props.minAccessLevel) {
    const required = MIN_TIER_ORDER[props.minAccessLevel];
    const actual = MIN_TIER_ORDER[actorLevel];
    return actual >= required;
  }
  // Defensive — neither prop supplied. Treat as deny so a misuse during
  // refactor doesn't accidentally expose a route.
  return false;
}
